import { getLabel } from '@apia/util';
import propsStore from '../../../../../../../../store/propsStore/propsStore';
import { TTrConfirmProps } from '../../../components/OverrideFieldRow';
import { IApiaField, IApiaForm } from '../../../../../../../../JSApi/types';
import { resolveComment } from './resolveComment';
import { getSortedKeys } from './getSortedKeys';
import { getFormsAssistantConfiguration } from '../../../getFormsAssistantConfiguration';
import { getSortedForms } from './getSortedForms';
import { TFieldValue } from './resolveResponseResult';
import { DataGridField } from '../../../../../../../../JSApi/fields';
import { TFormsAssistantConfigurations } from '../../../types';
import { ApiaUtil } from '@apia/components';

const resolveIfNeedConfirm = (
  fieldTitle: string,
  formName: string,
  fieldName: string,
  fieldValue: unknown,
  currentValue: string | number | boolean,
  currentValueLabel: string,
  newValue: TFieldValue,
  confirmProps: TTrConfirmProps[],
) => {
  if (
    fieldValue !== '' &&
    fieldValue !== null &&
    currentValue !== 'null' &&
    currentValue !== null &&
    currentValue !== ''
  ) {
    confirmProps.push({
      formName,
      fieldName,
      fieldTitle,
      currentValue,
      currentValueLabel,
      newValue,
    });
    return true;
  } else return false;
};

const resolveFieldValue = (field: IApiaField, value: TFieldValue) => {
  const actualValue: boolean | string | number | TFieldValue | null = value;
  const prevLabel: boolean | string | number | null | undefined = null;
  const fieldValue = field.getValue();

  const currentValue = fieldValue;
  let castedCurrentValue: string | number | boolean | null = null;
  if (
    typeof currentValue !== 'number' &&
    typeof currentValue !== 'boolean' &&
    typeof currentValue !== 'string'
  ) {
    castedCurrentValue = String(currentValue);
  } else castedCurrentValue = currentValue;
  return [fieldValue, castedCurrentValue, actualValue, prevLabel];
};

function addAndGetRow(grid: DataGridField) {
  return new Promise<number>((resolve, reject) => {
    const currentSize = grid.rowsCount;
    grid.addRow();

    let tries = 0;

    const i = setInterval(() => {
      if (tries++ < 200) {
        try {
          const row = grid.getRow(currentSize);
          if (row.length > 0) {
            clearInterval(i);
            resolve(currentSize);
          }
        } catch (_ignored) {
          //
        }
      } else {
        reject('Too many retries, rows not added');
        clearInterval(i);
      }
    }, 100);
  });
}

function clearGrid(grid: DataGridField) {
  return new Promise<DataGridField>((resolve, reject) => {
    (async () => {
      await grid.deleteGrid();

      let tries = 0;

      const i = setInterval(() => {
        if (tries++ < 200) {
          try {
            if (
              grid.getProperty('includeFirstRow')
                ? grid.rowsCount === 1
                : grid.rowsCount === 0
            ) {
              clearInterval(i);
              resolve(grid);
            }
          } catch (_ignored) {
            //
          }
        } else {
          reject('Too many retries, grid not cleared');
          clearInterval(i);
        }
      }, 100);
    })();
  });
}

async function fillGridField(
  field: DataGridField,
  value: Record<string, string>[],
  hasEmptyFirstRow: boolean,
) {
  let index = 0;
  for await (const gridRowValue of value) {
    let newIndex = 0;
    if (!hasEmptyFirstRow || index !== 0) {
      newIndex = await addAndGetRow(field);
    }
    const columns = field.columns;
    columns.forEach((gridColumn) => {
      const actualIncomingValue: string =
        gridRowValue[gridColumn.name] || gridRowValue[gridColumn.attName];
      if (actualIncomingValue) {
        const newRow = field.getRow(newIndex);
        newRow.forEach((rowField) => {
          if (
            rowField.getProperty('name') === gridColumn.name ||
            rowField.getProperty('name') === gridColumn.attName
          ) {
            rowField.setValue(
              rowField.valueType === 'N'
                ? ApiaUtil.instance.parsers.apiaNumber(
                    Number(actualIncomingValue),
                  )
                : actualIncomingValue,
            );
          }
        });
      }
    });
    index++;
  }
}

async function fillFields(
  fieldLabel: string,
  fieldsMap: Record<string, string>,
  form: IApiaForm,
  fields: Record<string, TFieldValue>,
  formName: string,
  confirmedValues: string[],
  confirmProps: TTrConfirmProps[],
) {
  const fieldName = fieldsMap[fieldLabel];
  const value = fields[fieldName];
  const result = fieldName.split('-_-', 2);
  let field;
  if (fieldName.includes(form.frmId!.split('_')[1])) {
    const overlap = form.frmId!.split('_').pop();
    const suffix = fieldName.replace(new RegExp('^' + overlap + '_'), '');

    const actualId = `${form.frmId!}_${suffix}`;
    field = form.getFieldById(actualId);
  } else {
    const actualField = form.getAllFields().find((fi) => {
      return fi.getProperty('gridTitle') === fieldName;
    });
    if (actualField) {
      field = actualField;
    } else {
      field = form.getField(
        result.length != 2 ? fieldName : result[1],
        result.length != 2 ? undefined : Number.parseInt(result[0]),
      );
    }
  }
  if (typeof value === 'string') {
    if (field) {
      const fieldProps = propsStore.getFieldProps(String(field.fldId));

      try {
        const [fieldValue, castedCurrentValue, actualValue, prevLabel] =
          resolveFieldValue(field, value);
        if (
          !resolveIfNeedConfirm(
            ((fieldProps.attributes as any).attLabel as string) ?? '',
            formName,
            fieldName,
            fieldValue,
            castedCurrentValue as string | number | boolean,
            (prevLabel ? prevLabel : castedCurrentValue) as string,
            value,
            confirmProps,
          )
        ) {
          field.setValue(
            field.valueType === 'N'
              ? ApiaUtil.instance.parsers.apiaNumber(
                  Number((actualValue !== null ? actualValue : value) as any),
                )
              : ((actualValue !== null ? actualValue : value) as any),
          );
          confirmedValues.push(fieldLabel);
        }
      } catch (e) {
        e;
        return;
      }
    } else {
      console.warn(`Field with name ${fieldName} not found.`);
    }
  } else {
    if (field) {
      const actualField = field as DataGridField;
      const clearedGrid = await clearGrid(actualField);
      const hasEmptyFirstRow = !!clearedGrid.getProperty('includeFirstRow');
      await fillGridField(clearedGrid, value, hasEmptyFirstRow);
    }
  }
}

async function fillForm(
  formName: string,
  filledFormsMap: Record<string, Record<string, TFieldValue>>,
  confirmedValues: string[],
  confirmProps: TTrConfirmProps[],
  formsAssistantConfigurations: TFormsAssistantConfigurations,
) {
  const form = window.ApiaFunctions.getForm(formName);
  const fields: Record<string, TFieldValue> = filledFormsMap[formName];
  if (form) {
    const formFieldMap: Record<
      string,
      { form: IApiaForm; field: IApiaField; rowIdx?: number }
    > = {};
    const fieldsMap: Record<string, string> = Object.keys(fields).reduce(
      (fieldsMap: Record<string, string>, fieldName) => {
        const result = fieldName.split('-_-', 2);
        let field;
        if (fieldName.includes(form.frmId!.split('_')[1])) {
          const overlap = form.frmId!.split('_').pop();
          const suffix = fieldName.replace(new RegExp('^' + overlap + '_'), '');

          const actualId = `${form.frmId!}_${suffix}`;
          field = form.getFieldById(actualId);
        } else {
          const actualField = form.getAllFields().find((fi) => {
            return fi.getProperty('gridTitle') === fieldName;
          });
          if (actualField) {
            field = actualField;
          } else {
            field = form.getField(
              result.length != 2 ? fieldName : result[1],
              result.length != 2 ? undefined : Number.parseInt(result[0]),
            );
          }
        }
        if (field) {
          const fieldProps = propsStore.getFieldProps(String(field.fldId));
          const key =
            result.length < 2
              ? (fieldProps.attributes.attLabel ?? fieldProps.name)
              : getLabel('lblAiActualGridFieldTitle', {
                  text: {
                    TOK1:
                      fieldProps.attributes.attLabel !== undefined &&
                      fieldProps.attributes.attLabel !== ''
                        ? fieldProps.attributes.attLabel
                        : result[1],
                    TOK2: result[0],
                  },
                }).text;
          fieldsMap[key] = fieldName;
          formFieldMap[key] = {
            form,
            field,
            ...(result.length > 1
              ? { rowIdx: Number.parseInt(result[0]) }
              : {}),
          };
        }
        return fieldsMap;
      },
      {},
    );
    const sortedFields: string[] = getSortedKeys(formFieldMap);
    for await (const fieldLabel of sortedFields) {
      fillFields(
        fieldLabel,
        fieldsMap,
        form,
        fields,
        formName,
        confirmedValues,
        confirmProps,
      );
    }
  } else if (
    Object.keys(filledFormsMap ?? {}).length > 1 &&
    formName === formsAssistantConfigurations.OBSERVATIONS.name &&
    fields[formsAssistantConfigurations.OBSERVATIONS.name.toLowerCase()]
  ) {
    resolveComment(formName, fields, confirmProps);
  } else {
    console.warn(`Form with name ${formName} not found.`);
  }
}

export async function fillForms(
  filledFormsMap: Record<string, Record<string, TFieldValue>>,
): Promise<{ confirmProps: TTrConfirmProps[]; confirmedValues: string[] }> {
  const formsAssistantConfigurations = getFormsAssistantConfiguration();
  const confirmProps: TTrConfirmProps[] = [];
  const confirmedValues: string[] = [];

  for await (const formName of getSortedForms(filledFormsMap ?? {})) {
    fillForm(
      formName,
      filledFormsMap,
      confirmedValues,
      confirmProps,
      formsAssistantConfigurations,
    );
  }

  return { confirmProps, confirmedValues };
}
